// Relink — point this Screen at a different physical device by entering the
// pairing code shown on that device. Two-step flow:
//
//   1. Operator enters the code → we POST /screens/{id}/validate-relink to
//      confirm it maps to an unpaired Screen row that isn't this one. The
//      response carries the target's device_model so the confirm step can
//      show what they're about to relink to.
//   2. Confirmation modal warns "this cannot be undone" — on accept, we POST
//      /screens/{id}/relink which atomically moves the target's device
//      identity onto this Screen and deletes the target row. The old device's
//      token is orphaned; its next /content call 404s and the webplayer
//      auto-registers, so the previous physical display visibly drops back
//      to the pairing-code page without manual intervention.

import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Loader2, AlertTriangle, Link as LinkIcon } from 'lucide-react'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@meno/ui/components/ui/dialog'
import { Button } from '@meno/ui/components/ui/button'
import { OtpInput } from '@meno/ui/components/ui/OtpInput'
import { validateRelinkCode, relinkScreen } from '@meno/ui/lib/api'

interface Props {
  open:        boolean
  /** Screen's opaque public_id (UUID) — used by the relink/validate endpoints. */
  screenId:    string
  /** Is hardware currently bound? Everything alarming in this dialog is about
   *  DISPLACING an existing device — the amber "already linked to another
   *  screen", the "cannot be undone", the destructive button. On a detached
   *  screen none of that is true: nothing is being replaced and nothing is
   *  lost, so the warning was pure invention and the operator was being asked
   *  to accept consequences that could not happen.
   *
   *  Defaults true so any caller that doesn't pass it keeps the cautious
   *  wording — a false reassurance is worse than a false warning. */
  hasDevice?:  boolean
  onClose:     () => void
  onRelinked?: () => void
  /** Offered when the server refuses because the CURRENT device is offline.
   *  That device may be gone for good (broken, replaced, storage wiped) and
   *  then can never come back online, so the refusal alone was a dead end.
   *  Called with the code already entered so the page can detach the old
   *  device and reopen this dialog with it. */
  onDetachInstead?: (code: string) => void
  /** Pre-fill and check this code on open — used to resume after a detach. */
  initialCode?: string
}

type Step = 'enter' | 'confirm'

export default function RelinkScreenDialog({
  open, screenId, hasDevice = true, onClose, onRelinked, onDetachInstead, initialCode,
}: Props) {
  const { t } = useTranslation()
  const [step, setStep]       = useState<Step>('enter')
  const [code, setCode]       = useState('')
  const [busy, setBusy]       = useState(false)
  const [error, setError]     = useState<string | null>(null)
  const [newModel, setNewModel] = useState<string | null>(null)
  // The refusal was "the current device is offline" (X-Relink-Blocked).
  const [blockedOffline, setBlockedOffline] = useState(false)

  const reset = () => {
    setStep('enter')
    setCode('')
    setBusy(false)
    setError(null)
    setNewModel(null)
    setBlockedOffline(false)
  }

  const handleClose = () => {
    if (busy) return
    reset()
    onClose()
  }

  const handleValidate = async (override?: string) => {
    const v = (override ?? code).trim().toUpperCase()
    if (v.length < 6) {
      setError(t('relink.codeRequired', 'Enter a pairing code.'))
      return
    }
    setBusy(true); setError(null)
    try {
      const { data } = await validateRelinkCode(screenId, v)
      setNewModel(data.device_model ?? data.new_device_name ?? null)
      setStep('confirm')
    } catch (e: any) {
      setBlockedOffline(e?.response?.headers?.['x-relink-blocked'] === 'current_device_offline')
      setError(e?.response?.data?.detail || t('relink.codeInvalid', 'Code is invalid or the device is already paired.'))
    } finally {
      setBusy(false)
    }
  }

  // Resuming after "Detach the old device": the screen is detached now, so the
  // same code validates as a plain attach — check it straight away.
  useEffect(() => {
    if (open && initialCode && initialCode.length >= 6) {
      setCode(initialCode)
      void handleValidate(initialCode)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialCode])

  const detachInstead = () => {
    const c = code.trim().toUpperCase()
    reset()
    onDetachInstead?.(c)
  }

  const handleConfirm = async () => {
    setBusy(true); setError(null)
    try {
      await relinkScreen(screenId, code.trim().toUpperCase())
      toast.success(hasDevice
        ? t('relink.success', 'Screen relinked. The previous device will return to its pairing code.')
        : t('relink.successAttached', 'Device attached to this screen.'))
      reset()
      onClose()
      onRelinked?.()
    } catch (e: any) {
      setBlockedOffline(e?.response?.headers?.['x-relink-blocked'] === 'current_device_offline')
      setError(e?.response?.data?.detail || t('relink.failed', 'Relink failed. Try again.'))
      setBusy(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={o => { if (!o) handleClose() }}>
      <DialogContent className="max-w-md">
        {step === 'enter' ? (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <LinkIcon className="w-4 h-4 text-primary" />
                {hasDevice
                  ? t('relink.title', 'Relink Screen')
                  : t('relink.titleAttach', 'Attach Device')}
              </DialogTitle>
              <DialogDescription>
                {hasDevice
                  ? t(
                      'relink.description',
                      'Enter the pairing code shown on the new device. The current device will be disconnected and will display a fresh pairing code.',
                    )
                  : t(
                      'relink.descriptionAttach',
                      'Enter the pairing code shown on the device you want to attach. This screen keeps its name, content and licence.',
                    )}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-2">
              <div className="rounded-2xl border border-border bg-muted/20 px-6 py-8 flex justify-center">
                <OtpInput
                  value={code}
                  onChange={v => { setError(null); setCode(v) }}
                  length={6}
                  type="alphanumeric"
                  separatorAfter={2}
                  invalid={!!error}
                  onComplete={v => handleValidate(v)}
                  autoFocus
                />
              </div>
              {error && <p className="text-xs text-destructive text-center">{error}</p>}
              {blockedOffline && onDetachInstead && <DetachInsteadHint onClick={detachInstead} />}
            </div>

            <DialogFooter>
              <Button variant="ghost" onClick={handleClose} disabled={busy}>
                {t('common.cancel', 'Cancel')}
              </Button>
              <Button onClick={() => handleValidate()} disabled={busy || code.length < 6} className="gap-2">
                {busy && <Loader2 className="w-4 h-4 animate-spin" />}
                {t('common.continue', 'Continue')}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className={`flex items-center gap-2 ${hasDevice ? 'text-destructive' : ''}`}>
                {hasDevice
                  ? <AlertTriangle className="w-4 h-4" />
                  : <LinkIcon className="w-4 h-4 text-primary" />}
                {hasDevice
                  ? t('relink.confirmTitle', 'Confirm Relink')
                  : t('relink.confirmTitleAttach', 'Confirm Device')}
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-3 py-2">
              {/* Only when something is actually being displaced. On a
                  detached screen this claimed a device was attached, called the
                  action irreversible, and painted it red — three statements
                  that were all false. */}
              {hasDevice ? (
                <div className="rounded-lg border border-amber-200 dark:border-amber-700/50 bg-amber-50 dark:bg-amber-900/20 p-3 text-sm text-amber-900 dark:text-amber-100">
                  {t(
                    'relink.confirmBody',
                    'This screen is already linked to another screen. Are you sure you want to relink it?',
                  )}
                  <div className="mt-2 font-medium">
                    {t('relink.cannotBeUndone', 'This action cannot be undone')}
                  </div>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  {t(
                    'relink.confirmBodyAttach',
                    'This screen has no device attached. Attaching this one keeps the screen’s name, content and licence.',
                  )}
                </p>
              )}
              {newModel && (
                <p className="text-xs text-muted-foreground">
                  {t('relink.linkingTo', 'Linking to')}: <span className="font-medium text-foreground">{newModel}</span>
                </p>
              )}
              {error && <p className="text-xs text-destructive">{error}</p>}
              {blockedOffline && onDetachInstead && <DetachInsteadHint onClick={detachInstead} />}
            </div>

            <DialogFooter>
              <Button variant="ghost" onClick={handleClose} disabled={busy}>
                {t('common.cancel', 'Cancel')}
              </Button>
              <Button
                variant={hasDevice ? 'destructive' : 'default'}
                onClick={handleConfirm}
                disabled={busy}
                className="gap-2"
              >
                {busy && <Loader2 className="w-4 h-4 animate-spin" />}
                {hasDevice
                  ? t('relink.confirmAction', 'Yes, Relink')
                  : t('relink.confirmActionAttach', 'Attach device')}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}

/** The way out when the current device can't come back: detach it (screen,
 *  content and licence are kept), then attach the new one with the same code. */
function DetachInsteadHint({ onClick }: { onClick: () => void }) {
  const { t } = useTranslation()
  return (
    <div className="rounded-lg border border-border bg-muted/30 px-3 py-2.5 flex items-center gap-3">
      <p className="text-xs text-muted-foreground flex-1">
        {t('relink.goneForGood', 'Old device gone for good? Detach it — the screen keeps its name, content and licence — then attach the new one with this code.')}
      </p>
      <Button size="sm" variant="outline" onClick={onClick} className="shrink-0">
        {t('relink.detachInstead', 'Detach old device')}
      </Button>
    </div>
  )
}
